<template>
    <main ref="mainRef" class="ap-main">
        <div class="ap-palco">
            <div class="ap-video">
                <vue-plyr v-if="showPlayer"
                          class="ap-plyr"
                          ref="plyr"
                          :key="playerKey"
                          :options="plyrOptions"
                          @ready="onPlayerReady"
                          @timeupdate="onPlayerTime"
                          @error="onPlayerError"
                          @ended="onPlayerEnded">
                    <video v-if="playerType==='mp4' && mp4Url" playsinline controls @ended="onPlayerEnded">
                        <source :src="mp4Url" type="video/mp4" />
                    </video>
                    <div v-else-if="playerType==='youtube' && youtubeId"
                         data-plyr-provider="youtube"
                         :data-plyr-embed-id="youtubeId"></div>
                    <div v-else class="ap-video-vazio">Vídeo indisponível para esta aula.</div>
                </vue-plyr>
                <div v-else-if="video.titulo !== 'Carregando...'" class="ap-video-vazio">Vídeo indisponível para esta aula.</div>
            </div>
        </div>

        <div class="ap-barra-aula">
            <div class="ap-barra-linha">
                <div class="ap-titulo-bloco">
                    <span v-if="secaoTitulo" class="ap-secao-label">Seção {{ secaoNumero }} · {{ secaoTitulo }}</span>
                    <div class="ap-titulo-linha">
                        <h1 class="ap-titulo" :title="video.titulo">{{ video.titulo }}</h1>
                        <span v-if="concluido" class="ap-selo">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                            Concluída
                        </span>
                    </div>
                </div>
                <div class="ap-acoes">
                    <button type="button" class="ap-btn ap-btn--contorno" :disabled="!aulaAnterior" @click="irPara(aulaAnterior)">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                        <span class="ap-ocultar-sm">Anterior</span>
                    </button>
                    <button type="button"
                            class="ap-btn ap-btn--contorno"
                            :class="{ 'is-concluida': concluido }"
                            :aria-pressed="concluido"
                            @click="alternarConclusaoVideo">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>
                        {{ concluido ? 'Concluída' : 'Marcar como concluída' }}
                    </button>
                    <button type="button" class="ap-btn ap-btn--primario" :disabled="!proximaAula" @click="avancar">
                        Próxima aula
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                </div>
            </div>
            <div class="ap-abas" role="tablist" aria-label="Conteúdo da aula">
                <button type="button" role="tab" class="ap-aba" :class="{ 'is-ativa': abaAtiva === 'visao-geral' }" @click="abaAtiva = 'visao-geral'">Visão geral</button>
                <button type="button" role="tab" class="ap-aba" :class="{ 'is-ativa': abaAtiva === 'notas' }" @click="abaAtiva = 'notas'">
                    Notas pessoais
                    <span v-if="anotacoes.length" class="ap-contador">{{ anotacoes.length }}</span>
                </button>
                <button type="button" role="tab" class="ap-aba" :class="{ 'is-ativa': abaAtiva === 'materiais' }" @click="abaAtiva = 'materiais'">
                    Materiais
                    <span v-if="anexos.length" class="ap-contador">{{ anexos.length }}</span>
                </button>
            </div>
        </div>

        <div class="ap-conteudo">
            <div v-show="abaAtiva === 'visao-geral'" class="ap-visao">
                <div class="ap-visao-topo">
                    <section class="ap-card ap-trilha">
                        <div class="ap-trilha-cabecalho">
                            <div class="ap-trilha-titulos">
                                <span class="ap-eyebrow">SOBRE A TRILHA</span>
                                <h2 class="ap-trilha-titulo">{{ tituloTrilha || 'Trilha' }}</h2>
                            </div>
                            <button type="button" class="ap-btn ap-btn--contorno ap-btn--sm" @click="$emit('ver-trilha')">
                                Detalhes da trilha
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
                            </button>
                        </div>
                        <p v-if="descricaoTrilha" class="ap-trilha-descricao">{{ descricaoTrilha }}</p>
                        <dl class="ap-trilha-dados">
                            <div v-if="autorTrilha" class="ap-dado">
                                <span class="ap-dado-icone"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg></span>
                                <div><dt>Instrutor</dt><dd>{{ autorTrilha }}</dd></div>
                            </div>
                            <div class="ap-dado">
                                <span class="ap-dado-icone"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 13 9 5 9-5" /></svg></span>
                                <div><dt>Seções</dt><dd>{{ totalSessoes }}</dd></div>
                            </div>
                            <div class="ap-dado">
                                <span class="ap-dado-icone"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m10 9 5 3-5 3V9Z" /></svg></span>
                                <div><dt>Aulas</dt><dd>{{ totalAulas }}</dd></div>
                            </div>
                            <div v-if="minutosTotais" class="ap-dado">
                                <span class="ap-dado-icone"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></span>
                                <div><dt>Duração total</dt><dd>{{ formatarDuracao(minutosTotais) }}</dd></div>
                            </div>
                        </dl>
                    </section>

                    <section class="ap-card ap-progresso-card">
                        <span class="ap-eyebrow">SEU PROGRESSO</span>
                        <div class="ap-progresso-numero">
                            <strong>{{ percentual }}%</strong>
                            <span>{{ totalConcluidas }} de {{ plural(totalAulas, 'aula', 'aulas') }}</span>
                        </div>
                        <div class="ap-progresso ap-progresso--lg" role="progressbar" :aria-valuenow="percentual" aria-valuemin="0" aria-valuemax="100">
                            <div class="ap-progresso-fill" :style="{ width: percentual + '%' }"></div>
                        </div>
                        <dl class="ap-progresso-dados">
                            <div><dt>Tempo restante</dt><dd>{{ minutosRestantes ? formatarDuracao(minutosRestantes) : '—' }}</dd></div>
                            <div v-if="secaoAtual">
                                <dt>Seção atual</dt>
                                <dd>Seção {{ secaoAtual.numero }} · {{ secaoAtual.concluidas }} de {{ secaoAtual.total }}</dd>
                            </div>
                        </dl>
                    </section>
                </div>

                <section class="ap-proximas">
                    <div class="ap-entre">
                        <h2 class="ap-h2">Próximas aulas</h2>
                        <span v-if="aulasRestantes" class="ap-muted ap-sm">{{ plural(aulasRestantes, 'restante', 'restantes') }}</span>
                    </div>
                    <ol v-if="proximasAulas.length" class="ap-proximas-lista">
                        <li v-for="(aula, i) in proximasAulas" :key="aula.id">
                            <button type="button" class="ap-card ap-proxima" :class="{ 'is-seguinte': i === 0 }" @click="irPara(aula)">
                                <span class="ap-proxima-thumb">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" class="is-fill"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z" /></svg>
                                    <span v-if="aula.duracaoMin" class="ap-proxima-duracao">{{ formatarDuracao(aula.duracaoMin) }}</span>
                                </span>
                                <span class="ap-proxima-texto">
                                    <span class="ap-proxima-tag">{{ i === 0 ? 'A seguir · ' : '' }}Aula {{ aula.numero }}</span>
                                    <span class="ap-proxima-titulo">{{ aula.titulo }}</span>
                                </span>
                            </button>
                        </li>
                    </ol>
                    <div v-else class="ap-card ap-fim">
                        <span class="ap-vazio-icone">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" /><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" /></svg>
                        </span>
                        <div class="ap-vazio-texto">
                            <strong>Esta é a última aula da trilha</strong>
                            <span>Marque como concluída para finalizar.</span>
                        </div>
                    </div>
                </section>
            </div>

            <div v-if="abaAtiva === 'notas'" class="ap-grid-2">
                <section class="ap-card">
                    <header class="ap-card-header">
                        <label class="ap-h3">Nova nota</label>
                    </header>
                    <div class="ap-editor">
                        <quill-editor v-model="novaAnotacaoHtml" :options="editorOptions" class="quill-wrapper" />
                    </div>
                    <footer class="ap-card-footer">
                        <div class="ap-tempo-campo">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                            <label for="ap-tempo">Marcar em</label>
                            <input id="ap-tempo"
                                   v-model="tempoManual"
                                   inputmode="numeric"
                                   placeholder="mm:ss"
                                   :aria-invalid="!!erroTempo"
                                   @input="tempoEditado = true" />
                            <button v-if="tempoEditado" type="button" class="ap-link" @click="usarTempoAtual">usar tempo atual</button>
                            <span v-if="erroTempo" class="ap-erro" role="alert">{{ erroTempo }}</span>
                        </div>
                        <button type="button" class="ap-btn ap-btn--primario" :disabled="notaVazia" @click="salvarAnotacao">Salvar nota</button>
                    </footer>
                </section>
                <section class="ap-card">
                    <header class="ap-card-header">
                        <h2 class="ap-h3">Suas notas</h2>
                        <span v-if="anotacoes.length" class="ap-muted ap-sm">{{ anotacoes.length === 1 ? '1 nota' : anotacoes.length + ' notas' }}</span>
                    </header>
                    <ul v-if="notasOrdenadas.length" class="ap-notas">
                        <li v-for="anot in notasOrdenadas" :key="anot.idAnotacaoCliente" class="ap-nota">
                            <button type="button" class="ap-chip-tempo" @click="irParaTempo(anot.nrTempoVideo)">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="is-fill"><path d="M7 4v16l13-8Z" /></svg>
                                {{ formatarTimeSpan(anot.nrTempoVideo) }}
                            </button>
                            <div class="ap-nota-corpo">
                                <div class="ap-nota-texto" v-html="sanitize(anot.dsAnotacao)"></div>
                                <span v-if="anot.dtInclusao" class="ap-muted ap-sm">{{ formatarData(anot.dtInclusao) }}</span>
                            </div>
                            <button type="button" class="ap-btn-icone" aria-label="Excluir nota" @click="deletarAnotacao(anot.idAnotacaoCliente)">
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg>
                            </button>
                        </li>
                    </ul>
                    <div v-else class="ap-vazio">
                        <span class="ap-vazio-icone ap-vazio-icone--lg">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                        </span>
                        <strong>Nenhuma nota ainda</strong>
                        <span>Anote ideias enquanto assiste. Cada nota fica presa ao minuto do vídeo para você voltar depois.</span>
                    </div>
                </section>
            </div>

            <div v-show="abaAtiva === 'materiais'">
                <template v-if="anexos.length">
                    <div class="ap-materiais-topo">
                        <span class="ap-texto"><strong>{{ anexos.length }}</strong> {{ anexos.length === 1 ? 'material' : 'materiais' }} nesta aula</span>
                        <label class="ap-busca">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                            <span class="ap-sr">Buscar material</span>
                            <input v-model="buscaMaterial" type="search" placeholder="Buscar material" />
                        </label>
                    </div>
                    <ul v-if="materiaisFiltrados.length" class="ap-materiais">
                        <li v-for="anexo in materiaisFiltrados" :key="anexo.idAnexoRecurso">
                            <button type="button" class="ap-card ap-material" :title="anexo.nmAnexo" @click="$emit('baixar-anexo', anexo.idAnexoRecurso, anexo.nmAnexo)">
                                <span class="ap-tipo" :class="tipoMaterial(anexo.nmAnexo).classe">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6" /></svg>
                                    <span>{{ tipoMaterial(anexo.nmAnexo).rotulo }}</span>
                                </span>
                                <span class="ap-material-texto">
                                    <span class="ap-material-nome">{{ anexo.nmAnexo }}</span>
                                    <span class="ap-muted ap-sm">{{ tipoMaterial(anexo.nmAnexo).meta }}</span>
                                </span>
                                <span class="ap-material-baixar" aria-hidden="true">
                                    <svg viewBox="0 0 24 24"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
                                </span>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="ap-muted ap-sm">Nenhum material encontrado para "{{ buscaMaterial }}".</p>
                </template>
                <div v-else class="ap-card ap-vazio">
                    <span class="ap-vazio-icone ap-vazio-icone--lg">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /></svg>
                    </span>
                    <strong>Nenhum material nesta aula</strong>
                    <span>Quando o instrutor anexar apostilas, modelos ou planilhas, eles aparecem aqui para download.</span>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
    import axios from 'axios'
    import Swal from 'sweetalert2'
    import { urlApi, avisoErro, avisoToast } from '@/config/global'
    import { quillEditor } from 'vue-quill-editor'
    import 'quill/dist/quill.snow.css'
    import DOMPurify from 'dompurify'

    export default {
        name: 'VideoViewer',
        props: {
            idVideo: { type: [String, Number], required: true },
            idTreinamento: { type: [String, Number], required: true },
            descricaoTrilha: { type: String, default: '' },
            tituloTrilha: { type: String, default: '' },
            autorTrilha: { type: String, default: '' },
            totalSessoes: { type: Number, default: 0 },
            totalAulas: { type: Number, default: 0 },
            aulaAnterior: { type: Object, default: null },
            proximaAula: { type: Object, default: null },
            secaoNumero: { type: Number, default: 0 },
            secaoTitulo: { type: String, default: '' },
            percentual: { type: Number, default: 0 },
            totalConcluidas: { type: Number, default: 0 },
            minutosTotais: { type: Number, default: 0 },
            minutosRestantes: { type: Number, default: 0 },
            aulasRestantes: { type: Number, default: 0 },
            proximasAulas: { type: Array, default: () => [] },
            secaoAtual: { type: Object, default: null },
            anexos: { type: Array, default: () => [] },
        },
        components: { quillEditor },
        data() {
            return {
                abaAtiva: 'visao-geral',
                video: { titulo: 'Carregando...', url: '', descricao: '' },
                ultimoIdVideoCarregado: null,
                anotacoes: [],
                tempoManual: '',
                tempoEditado: false,
                erroTempo: '',
                buscaMaterial: '',
                concluido: false,
                idProgressoCliente: null,
                playerType: '',
                mp4Url: '',
                youtubeId: '',
                playerReady: false,
                player: null,
                ytOnStateChange: null,
                ytState: -1,
                autoMarkDoneForToken: -1,
                watchEndTimer: null,
                autoDoneThreshold: 0.985,
                loadToken: 0,
                currentTimeSec: 0,
                showPlayer: false,
                evOnEnded: null,
                evOnPlaying: null,
                evOnPause: null,
                evOnSeeked: null,
                evOnWaiting: null,
                evOnStalled: null,
                evOnTU: null,
                plyrOptions: {
                    controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen'],
                    youtube: { rel: 0, noCookie: true, modestbranding: 1 },
                    storage: { enabled: false },
                },
                novaAnotacaoHtml: '',
                editorOptions: {
                    theme: 'snow',
                    placeholder: 'Digite algo…',
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            ['link', 'blockquote', 'code'],
                            ['clean'],
                        ],
                    },
                },
                lastCur: 0,
                isPostingTempo: false,
                lastCurAt: 0,
                stableNearEndMs: 1800,
                nearEndLeeway: 1.5,
                bootstrapTimer: null,
                bootstrapTries: 0,
                probeTimer: null,
                timerAtualizacao: null,
                lastPostedSec: 0,
                
            }
        },

        computed: {
            playerKey() {
                return `${this.playerType}:${this.idVideo}:${this.youtubeId || this.mp4Url}`
            },
            notaVazia() {
                const html = (this.novaAnotacaoHtml || '').replace(/<[^>]*>/g, '').trim();
                return !html;
            },
            materiaisFiltrados() {
                const termo = (this.buscaMaterial || '').trim().toLowerCase();
                if (!termo) return this.anexos;
                return this.anexos.filter(anexo => (anexo.nmAnexo || '').toLowerCase().includes(termo));
            },
            notasOrdenadas() {
                return [...this.anotacoes].sort((a, b) => this.segundosDe(a.nrTempoVideo) - this.segundosDe(b.nrTempoVideo));
            },
        },

        methods: {
            irPara(aula) {
                if (aula?.id) this.$emit('selecionar-aula', aula.id);
            },

            avancar() {
                if (!this.proximaAula?.id) return;
                const destino = this.proximaAula.id;
                if (!this.concluido) {
                    this.$emit('atualizar-progresso', this.idVideo);
                    this.alternarConclusaoVideo(false);
                }
                this.$emit('selecionar-aula', destino);
            },

            plural(quantidade, singular, pluralTxt) {
                return `${quantidade} ${quantidade === 1 ? singular : pluralTxt}`;
            },

            formatarDuracao(minutos) {
                if (!minutos) return '—';
                if (minutos < 60) return `${minutos} min`;
                const horas = Math.floor(minutos / 60);
                const resto = minutos % 60;
                return resto ? `${horas}h ${resto}min` : `${horas}h`;
            },

            tipoMaterial(nome) {
                const extensao = (nome || '').split('.').pop().toLowerCase();
                if (extensao === 'pdf') return { rotulo: 'PDF', meta: 'Documento PDF', classe: 'ap-tipo--pdf' };
                if (['doc', 'docx', 'odt'].includes(extensao)) return { rotulo: 'DOC', meta: 'Documento', classe: 'ap-tipo--doc' };
                if (['xls', 'xlsx'].includes(extensao)) return { rotulo: 'XLS', meta: 'Planilha', classe: 'ap-tipo--xls' };
                if (extensao === 'csv') return { rotulo: 'CSV', meta: 'Planilha', classe: 'ap-tipo--xls' };
                if (['ppt', 'pptx'].includes(extensao)) return { rotulo: 'PPT', meta: 'Apresentação', classe: 'ap-tipo--ppt' };
                if (['png', 'jpg', 'jpeg'].includes(extensao)) return { rotulo: 'IMG', meta: 'Imagem', classe: 'ap-tipo--img' };
                if (extensao === 'mp4') return { rotulo: 'VID', meta: 'Vídeo', classe: 'ap-tipo--img' };
                return { rotulo: 'ARQ', meta: 'Arquivo', classe: '' };
            },

            usarTempoAtual() {
                this.tempoEditado = false;
                this.erroTempo = '';
                this.tempoManual = this.formatarTempoSeg(this.currentTimeSec);
            },

            segundosDe(valor) {
                const partes = String(valor || '0').split(':').map(n => parseInt(n, 10) || 0);
                if (partes.length === 3) return partes[0] * 3600 + partes[1] * 60 + partes[2];
                if (partes.length === 2) return partes[0] * 60 + partes[1];
                return partes[0] || 0;
            },

            formatarTempoSeg(segundos) {
                const total = Math.floor(Number(segundos) || 0);
                const h = Math.floor(total / 3600);
                const m = Math.floor((total % 3600) / 60);
                const s = total % 60;
                const pad = n => String(n).padStart(2, '0');
                return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
            },

            formatarData(valor) {
                const data = new Date(valor);
                if (Number.isNaN(data.getTime())) return '';
                const hoje = new Date();
                const ontem = new Date(hoje);
                ontem.setDate(hoje.getDate() - 1);
                const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                if (data.toDateString() === hoje.toDateString()) return `Hoje, ${hora}`;
                if (data.toDateString() === ontem.toDateString()) return `Ontem, ${hora}`;
                return `${data.toLocaleDateString('pt-BR')}, ${hora}`;
            },

            irParaTempo(tempo) {
                this.seekTo(tempo);
                this.$refs.mainRef?.scrollTo({ top: 0, behavior: 'smooth' });
            },

            destroyPlyr() {
                try { this.$refs.plyr?.player?.destroy?.() } catch (e) { avisoErro('Erro ao destruir viewer.') }
                this.player = null
                this.playerReady = false
                this.stopAutoPost()
                this.stopWatchEnd()
                if (this.probeTimer) { clearInterval(this.probeTimer); this.probeTimer = null }
                if (this.bootstrapTimer) { clearInterval(this.bootstrapTimer); this.bootstrapTimer = null; this.bootstrapTries = 0 }

                if (this.mp4Url && this.mp4Url.startsWith('blob:')) {
                    URL.revokeObjectURL(this.mp4Url)
                }
                this.mp4Url = ''
            },

            marcarIndisponivel(token, titulo, descricao) {
                if (token !== this.loadToken) return
                this.playerType = ''
                this.showPlayer = false
                this.video = { titulo, descricao, url: '' }
                this.$emit('aula-indisponivel', this.idVideo)
            },

            async loadVideo() {
                const token = ++this.loadToken
                this.autoMarkDoneForToken = -1
                this.stopWatchEnd();
                this.showPlayer = false
                this.destroyPlyr();
                this.lastPostedSec = 0
                this.isPostingTempo = false

                try {
                    const res = await axios.get(`${urlApi}/SollAcademy/GetRecursosSessoes`, {
                        params: { idRecursoSessao: this.idVideo },
                    })

                    if (!res.data?.success || !res.data?.data?.rows?.length) {
                        if (token !== this.loadToken) return
                        this.marcarIndisponivel(token, 'Vídeo não encontrado', 'Não conseguimos localizar esse vídeo.')
                        return
                    }

                    const recurso = res.data.data.rows[0].item
                    this.video.titulo = recurso.nmRecurso
                    this.video.descricao = recurso.dsRecurso || ''
                    const url = String(recurso.urlRecurso || '').trim()

                    if (String(recurso.dsFormato || '').toLowerCase() === 'youtube') {
                        this.youtubeId = this.parseYoutubeId(url)
                        this.mp4Url = ''
                        this.playerType = this.youtubeId ? 'youtube' : ''
                    } else if (url) {
                        this.youtubeId = ''
                        const streamUrl = `${urlApi}/SollAcademyGoogle/StreamVideo`
                        this.mp4Url = `${streamUrl}?path=${encodeURIComponent(url)}`
                        this.playerType = 'mp4'
                    } else {
                        this.youtubeId = ''
                        this.mp4Url = ''
                        this.playerType = ''
                    }

                    if (!this.playerType) {
                        if (token !== this.loadToken) return
                        this.marcarIndisponivel(token, recurso.nmRecurso || 'Vídeo indisponível', 'Esta aula não tem um vídeo que possa ser reproduzido.')
                        return
                    }

                    if (token !== this.loadToken) return

                    await this.$nextTick()
                    this.showPlayer = true
                    await this.$nextTick()
                    this.ensureBootstrap('afterShowPlayer')

                    if (!this.bootstrapTimer) {
                        this.bootstrapTries = 0
                        this.bootstrapTimer = setInterval(() => {
                            if (this.ensureBootstrap('bootstrapPoll')) {
                                clearInterval(this.bootstrapTimer)
                                this.bootstrapTimer = null
                                this.bootstrapTries = 0
                            } else if (++this.bootstrapTries > 40) {
                                clearInterval(this.bootstrapTimer)
                                this.bootstrapTimer = null
                            }
                        }, 300)
                    }

                    window.dispatchEvent(new Event('resize'))
                    this.getAnotacoes()
                    this.getProgressoCliente()
                } catch (err) {
                    if (token !== this.loadToken) return
                    console.error(err)
                    avisoToast('Erro ao carregar vídeo!', 'warning')
                    this.playerType = ''
                    this.video = { titulo: 'Erro ao carregar', descricao: 'Houve um erro ao buscar os dados do vídeo.', url: '' }
                }
            },

            getAnotacoes() {
                axios.get(`${urlApi}/SollAcademy/GetAnotacoesClientes`, {
                    params: { idRecursoSessao: this.idVideo },
                })
                    .then(res => {
                        if (res.data.success) {
                            this.anotacoes = res.data.data.rows.map(r => r.item)
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao carregar anotações:', err)
                        avisoToast('Erro ao carregar anotações!', 'warning')
                    })
            },

            registraTempo() {
                try {
                    if (!this.playerReady) return
                    if (!this.idProgressoCliente) return

                    const { cur } = this.getTimes()
                    const sec = Math.floor(cur || 0)
                    if (sec <= this.lastPostedSec) return
                    if (this.isPostingTempo) return

                    const h = Math.floor(sec / 3600)
                    const m = Math.floor((sec % 3600) / 60)
                    const s = Math.floor(sec % 60)
                    const hms = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`

                    this.isPostingTempo = true
                    axios.post(`${urlApi}/SollAcademy/AtualizaTempoProgressoCliente`, {
                        idProgressoCliente: this.idProgressoCliente,
                        nrTempoAssistido: hms, 
                    }, {showLoading: false})
                        .then(() => { this.lastPostedSec = sec })
                        .catch(err => { console.warn('Falha ao atualizar tempo:', err) })
                        .finally(() => { this.isPostingTempo = false })
                } catch (e) {
                    console.warn('Erro registraTempo:', e)
                }
            },

            sanitize(html) {
                return DOMPurify.sanitize(html, {
                    ALLOWED_TAGS: ['b', 'i', 'strong', 'em', 'u', 'p', 'div', 'br', 'ol', 'ul', 'li', 'blockquote', 'code', 'a'],
                    ALLOWED_ATTR: ['href', 'target', 'rel'],
                })
            },

            salvarAnotacao() {
                const conteudoHtml = (this.novaAnotacaoHtml || '').trim()
                if (!conteudoHtml || this.notaVazia) return

                let tempo = (this.tempoManual || '').trim()
                if (!tempo) {
                    tempo = this.getTempoAtualDoVideo()
                } else if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(tempo)) {
                    this.erroTempo = 'Use mm:ss ou hh:mm:ss';
                    return
                }
                this.erroTempo = '';
                const partes = tempo.split(':').map(p => p.padStart(2, '0'))
                while (partes.length < 3) partes.unshift('00')
                tempo = partes.join(':')

                const htmlSeguro = this.sanitize(conteudoHtml)

                const payload = {
                    DadosAnotacaoCliente: {
                        idRecursoSessao: this.idVideo,
                        dsAnotacao: htmlSeguro,
                        nrTempoVideo: tempo,
                    },
                }

                axios.post(`${urlApi}/SollAcademy/PostAnotacoesCliente`, payload)
                    .then(res => {
                        if (res.data.success) {
                            this.novaAnotacaoHtml = ''
                            this.tempoEditado = false
                            this.erroTempo = ''
                            this.tempoManual = this.formatarTempoSeg(this.currentTimeSec)
                            this.getAnotacoes()
                            avisoToast('Anotação salva com sucesso!', 'success')
                        } else {
                            avisoToast('Erro ao salvar anotação!', 'warning')
                        }
                    })
                    .catch(err => {
                        console.error('Erro ao salvar anotação:', err)
                        avisoToast('Erro ao salvar anotação!', 'danger')
                    })
            },

            deletarAnotacao(idAnotacaoCliente) {
                Swal.fire({
                    title: 'Tem certeza?',
                    text: 'Essa ação não poderá ser desfeita!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Sim, excluir!',
                    cancelButtonText: 'Cancelar',
                }).then((result) => {
                    if (result.isConfirmed) {
                        axios.post(`${urlApi}/SollAcademy/DeleteAnotacoesCliente`, { idAnotacaoCliente })
                            .then(res => {
                                if (res.data.success) {
                                    avisoToast('Anotação excluída com sucesso!', 'success')
                                    this.getAnotacoes()
                                } else {
                                    avisoToast('Erro ao excluir anotação!', 'warning')
                                }
                            })
                            .catch(err => {
                                console.error('Erro ao excluir anotação:', err)
                                avisoToast('Erro ao excluir anotação!', 'danger')
                            })
                    }
                })
            },

            getProgressoCliente() {
                axios.get(`${urlApi}/SollAcademy/GetProgressosClientes`, {
                    params: { idRecursoSessao: this.idVideo },
                }).then(res => {
                    if (res.data.success && res.data.data.rows.length > 0) {
                        const progresso = res.data.data.rows[0].item
                        this.concluido = progresso.flConcluido
                        this.idProgressoCliente = progresso.idProgressoCliente
                    } else {
                        const payload = {
                            DadosProgressoCliente: { idRecursoSessao: this.idVideo, flConcluido: false },
                        }
                        axios.post(`${urlApi}/SollAcademy/PostProgressoCliente`, payload)
                            .then(res => {
                                if (res.data.success) {
                                    this.idProgressoCliente = res.data.data
                                } else {
                                    avisoToast('Erro ao gerar progresso.', 'warning')
                                }
                            })
                    }
                }).catch(err => {
                    console.error('Erro ao buscar progresso:', err)
                })
            },

            alternarConclusaoVideo(emitirProgresso = true) {
                const emitir = emitirProgresso !== false;
                if (this.concluido && this.idProgressoCliente) {
                    axios.post(`${urlApi}/SollAcademy/DeleteProgressoClienteConcluido`, {
                        idProgressoCliente: this.idProgressoCliente,
                    }).then(res => {
                        if (res.data.success) {
                            this.idProgressoCliente = res.data.data
                            this.concluido = false
                            if (emitir) this.$emit('atualizar-progresso', this.idVideo)
                            avisoToast('Progresso removido com sucesso.', 'info')
                        } else {
                            avisoToast('Erro ao remover progresso.', 'warning')
                        }
                    }).catch(err => {
                        console.error('Erro ao deletar progresso:', err)
                        avisoToast('Erro ao remover progresso.', 'error')
                    })
                } else {
                    const payload = {
                        DadosProgressoCliente: { idRecursoSessao: this.idVideo, flConcluido: true },
                    }
                    axios.post(`${urlApi}/SollAcademy/PostProgressoCliente`, payload)
                        .then(res => {
                            if (res.data.success) {
                                this.idProgressoCliente = res.data.data
                                this.concluido = true
                                if (emitir) this.$emit('atualizar-progresso', this.idVideo)
                                avisoToast('Progresso marcado como concluído!', 'success')
                            } else {
                                avisoToast('Erro ao marcar como concluído.', 'warning')
                            }
                        })
                        .catch(err => {
                            console.error('Erro ao salvar progresso:', err)
                            avisoToast('Erro ao marcar como concluído.', 'error')
                        })
                }
            },

            formatarTimeSpan(str) {
                if (!str) return '00:00'
                const [h, m, s] = str.split(':').map(Number)
                if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
                return `${m}:${String(s).padStart(2, '0')}`
            },

            onPlayerTime() {
                const { cur } = this.getTimes()
                this.currentTimeSec = Math.floor(cur || 0)
                if (!this.tempoEditado) this.tempoManual = this.formatarTempoSeg(this.currentTimeSec)
                this.forceDoneCheck('timeupdate')
            },

            onPlayerReady(evt) {
                const p = evt?.detail?.plyr || this.$refs.plyr?.player || null
                if (!p) return

                this.player = p
                this.playerReady = true

                this.stopWatchEnd()
                if (this.timerAtualizacao) { clearInterval(this.timerAtualizacao); this.timerAtualizacao = null }

                try { p.off('ended', this.evOnEnded) } catch (e) { void e; }
                try { p.off('seeked', this.evOnSeeked) } catch (e) { void e; }
                try { p.off('playing', this.evOnPlaying) } catch (e) { void e; }
                try { p.off('pause', this.evOnPause) } catch (e) { void e; }
                try { p.off('waiting', this.evOnWaiting) } catch (e) { void e; }
                try { p.off('stalled', this.evOnStalled) } catch (e) { void e; }

                this.evOnEnded = () => { this.registraTempo(); this.stopAutoPost(); this.avisoFimVideo?.(); this.onPlayerEnded('plyr_ended') }
                this.evOnSeeked = () => { this.registraTempo() }
                this.evOnPause = () => { this.registraTempo(); this.stopAutoPost(); this.stopWatchEnd() }
                this.evOnPlaying = () => { this.startWatchEnd(); this.startAutoPost(); this.forceDoneCheck('plyr_playing') }
                this.evOnWaiting = () => { this.forceDoneCheck('plyr_waiting') }
                this.evOnStalled = () => { this.forceDoneCheck('plyr_stalled') }

                p.on('ended', this.evOnEnded)
                p.on('seeked', this.evOnSeeked)
                p.on('playing', this.evOnPlaying)
                p.on('pause', this.evOnPause)
                p.on('waiting', this.evOnWaiting)
                p.on('stalled', this.evOnStalled)

                this.startWatchEnd()
                this.attachYouTubeStateListener()
                this.armProbe()
                this.ensureBootstrap('onPlayerReady')
            },

            onPlayerError(e) {
                console.warn('Plyr error', e)
                if (this.playerType === 'youtube') this.showPlayer = false
            },

            onPlayerEnded() {
                if (this.autoMarkDoneForToken === this.loadToken) return
                this.autoMarkDoneForToken = this.loadToken
                if (!this.concluido) this.alternarConclusaoVideo()
            },

            getTempoAtualDoVideo() {
                const sec = this.currentTimeSec || Math.floor(this.$refs.plyr?.player?.currentTime || 0)
                const h = Math.floor(sec / 3600)
                const m = Math.floor((sec % 3600) / 60)
                const s = Math.floor(sec % 60)
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
            },

            seekTo(timeStr) {
                const p = this.player || this.$refs.plyr?.player
                if (!p) return
                const parts = (timeStr || '0:00').split(':').map(n => parseInt(n, 10))
                let seconds = 0
                if (parts.length === 3) seconds = parts[0] * 3600 + parts[1] * 60 + parts[2]
                else seconds = parts[0] * 60 + parts[1]
                p.currentTime = seconds
                p.play?.()
            },

            parseYoutubeId(url) {
                if (!url) return ''
                try {
                    const u = new URL(url)
                    return u.searchParams.get('v')
                        || (u.hostname.includes('youtu.be') ? u.pathname.slice(1) : '')
                        || (u.pathname.startsWith('/shorts/') ? u.pathname.split('/')[2] : '')
                        || (u.pathname.startsWith('/embed/') ? u.pathname.split('/')[2] : '')
                } catch (e) {
                    const m = url.match(/(?:v=|youtu\.be\/|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/)
                    return m ? m[1] : ''
                }
            },

            startWatchEnd() {
                this.stopWatchEnd()
                this.watchEndTimer = setInterval(() => this.forceDoneCheck('watchdog'), 1000)
            },

            stopWatchEnd() {
                if (this.watchEndTimer) {
                    clearInterval(this.watchEndTimer)
                    this.watchEndTimer = null
                }
            },

            getTimes() {
                const p = this.player || this.$refs.plyr?.player
                if (!p) return { cur: 0, dur: 0 }

                if (this.playerType === 'youtube' && p.embed) {
                    const get = (fn) => { try { return Number(fn.call(p.embed)) || 0 } catch { return 0 } }
                    const cur = typeof p.embed.getCurrentTime === 'function' ? get(p.embed.getCurrentTime) : Number(p.currentTime || 0)
                    const dur = typeof p.embed.getDuration === 'function' ? get(p.embed.getDuration) : Number(p.duration || 0)
                    return { cur, dur }
                }
                return { cur: Number(p.currentTime || 0), dur: Number(p.duration || 0) }
            },

            maybeMarkDone() {
                if (this.autoMarkDoneForToken === this.loadToken || this.concluido) return
                const { cur, dur } = this.getTimes()
                const ratio = dur > 0 ? (cur / dur) : 0
                const ytEnded = this.playerType === 'youtube' && this.isYouTubeEnded()
                const threshold = this.playerType === 'youtube' ? 0.95 : 0.97
                if (ytEnded || (dur > 0 && (cur >= dur - 1 || ratio >= threshold))) {
                    this.onPlayerEnded()
                }
            },

            attachYouTubeStateListener() {
                const tryAttach = () => {
                    const p = this.player || this.$refs.plyr?.player
                    if (!(this.playerType === 'youtube' && p?.embed)) return false

                    try {
                        if (this.ytOnStateChange && typeof p.embed.removeEventListener === 'function') {
                            p.embed.removeEventListener('onStateChange', this.ytOnStateChange)
                        }
                    } catch (e) { void e; }

                    const cb = (e) => {
                        this.ytState = e?.data
                        if (e && e.data === 0) return this.onPlayerEnded('yt_state_ended')
                        if (e && (e.data === 1 || e.data === 3)) this.forceDoneCheck('yt_play_or_buffer')
                    }

                    try {
                        if (typeof p.embed.addEventListener === 'function') {
                            p.embed.addEventListener('onStateChange', cb)
                            this.ytOnStateChange = cb
                            return true
                        }
                    } catch (e) { void e; }

                    return false
                }

                if (!tryAttach()) {
                    const h = setInterval(() => { if (tryAttach()) clearInterval(h) }, 300)
                    setTimeout(() => { clearInterval(h) }, 8000)
                }
            },

            isYouTubeEnded() {
                try {
                    const p = this.player || this.$refs.plyr?.player
                    const s = p?.embed?.getPlayerState?.call(p.embed)
                    return s === 0
                } catch { return false }
            },

            readYTState() {
                try {
                    const p = this.player || this.$refs.plyr?.player
                    return p?.embed?.getPlayerState?.call(p.embed)
                } catch { return undefined }
            },

            forceDoneCheck() {
                if (this.autoMarkDoneForToken === this.loadToken || this.concluido) return

                const now = Date.now()
                const { cur, dur } = this.getTimes()
                const ratio = dur > 0 ? (cur / dur) : 0

                if (Math.abs(cur - this.lastCur) >= 0.25) {
                    this.lastCur = cur
                    this.lastCurAt = now
                }

                const isYouTube = this.playerType === 'youtube'
                const threshold = isYouTube ? 0.95 : this.autoDoneThreshold
                const leeway = isYouTube ? this.nearEndLeeway : 0.75

                const nearEndByTime = dur > 0 && (dur - cur) <= leeway
                const nearEndByRatio = dur > 0 && ratio >= threshold
                const nearEnd = nearEndByTime || nearEndByRatio

                if (isYouTube) {
                    const yt = this.readYTState()
                    if (yt !== undefined) this.ytState = yt

                    const ytEnded = this.ytState === 0
                    const ytPlayOrBuf = this.ytState === 1 || this.ytState === 3
                    const stalledNearEnd = nearEnd && (now - this.lastCurAt) >= this.stableNearEndMs

                    if (ytEnded) return this.onPlayerEnded()
                    if (stalledNearEnd && ytPlayOrBuf) return this.onPlayerEnded()
                    return
                }

                if (nearEnd) return this.onPlayerEnded()
            },

            ensureBootstrap() {
                const comp = this.$refs.plyr
                const p = comp?.player
                if (!p) return false

                if (this.playerReady && this.player === p) return true

                this.player = p
                this.playerReady = true

                try { p.off('ended', this.evOnEnded) } catch (e) { void e; }
                try { p.off('seeked', this.evOnSeeked) } catch (e) { void e; }
                try { p.off('timeupdate', this.evOnTU) } catch (e) { void e; }
                try { p.off('playing', this.evOnPlaying) } catch (e) { void e; }
                try { p.off('pause', this.evOnPause) } catch (e) { void e; }

                this.evOnEnded = () => { this.registraTempo(); this.stopAutoPost(); this.onPlayerEnded('plyr_ended') }
                this.evOnSeeked = () => { this.registraTempo() }
                this.evOnTU = () => { }
                this.evOnPlaying = () => { this.startWatchEnd(); this.startAutoPost(); this.forceDoneCheck('plyr_playing') }
                this.evOnPause = () => { this.registraTempo(); this.stopAutoPost(); this.stopWatchEnd() }

                p.on('ended', this.evOnEnded)
                p.on('seeked', this.evOnSeeked)
                p.on('timeupdate', this.evOnTU)
                p.on('playing', this.evOnPlaying)
                p.on('pause', this.evOnPause)
                p.on('waiting', () => this.forceDoneCheck('plyr_waiting'))
                p.on('stalled', () => this.forceDoneCheck('plyr_stalled'))

                this.attachYouTubeStateListener()
                this.startWatchEnd()
                this.armProbe()
                return true
            },

            armProbe() {
                if (this.probeTimer) { clearInterval(this.probeTimer); this.probeTimer = null }
                if (this.playerType !== 'youtube') return
                this.probeTimer = setInterval(() => {
                    this.forceDoneCheck('probe')
                }, 800)
            },

            startAutoPost() {
                if (this.timerAtualizacao) { clearInterval(this.timerAtualizacao); this.timerAtualizacao = null }
                this.timerAtualizacao = setInterval(() => this.registraTempo(), 10000)
            },

            stopAutoPost() {
                if (this.timerAtualizacao) { clearInterval(this.timerAtualizacao); this.timerAtualizacao = null }
            },
        },

        mounted() {
            this.loadVideo()
        },

        watch: {
            idVideo(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.abaAtiva = 'visao-geral';
                    this.buscaMaterial = '';
                    this.tempoEditado = false;
                    this.erroTempo = '';
                    this.tempoManual = '00:00';
                    this.novaAnotacaoHtml = '';
                    this.loadVideo();
                    this.$refs.mainRef?.scrollTo({ top: 0, behavior: 'smooth' });
                }
            },
        },

        beforeDestroy() {
            try {
                const p = this.$refs?.plyr?.player
                if (p) {
                    if (this.evOnEnded) p.off('ended', this.evOnEnded)
                    if (this.evOnSeeked) p.off('seeked', this.evOnSeeked)
                    if (this.evOnTU) p.off('timeupdate', this.evOnTU)
                    if (this.evOnPlaying) p.off('playing', this.evOnPlaying)
                    if (this.evOnPause) p.off('pause', this.evOnPause)
                    if (p.embed && this.ytOnStateChange && typeof p.embed.removeEventListener === 'function') {
                        p.embed.removeEventListener('onStateChange', this.ytOnStateChange)
                    }
                }
            } catch (e) { void e; }
            this.stopWatchEnd()
            if (this.timerAtualizacao) clearInterval(this.timerAtualizacao)
            this.timerAtualizacao = null
            this.ytOnStateChange = null
            this.destroyPlyr()
        },
    }
</script>

<style scoped>
    .ap-main {
        --ap-video-h: min(62vh, 760px);
        min-width: 0;
        min-height: 0;
        height: 100%;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        background: var(--ap-fundo, #f8fafc);
        color: var(--ap-texto, #0f172a);
        font-family: 'Poppins', system-ui, sans-serif;
    }

    .ap-main svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
        flex-shrink: 0;
    }

    .ap-main svg.is-fill { fill: currentColor; stroke: none; }
    .ap-muted { color: var(--ap-muted); }
    .ap-sm { font-size: 12px; }
    .ap-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; color: var(--ap-muted); }
    .ap-h2 { margin: 0; font-size: 16px; font-weight: 600; }
    .ap-h3 { margin: 0; font-size: 15px; font-weight: 600; }
    .ap-texto { margin: 0; font-size: 14px; line-height: 1.65; color: var(--ap-texto-3); white-space: pre-line; }
    .ap-texto strong { color: var(--ap-texto); }
    .ap-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

    .ap-palco { flex-shrink: 0; background: #000; display: flex; justify-content: center; }
    .ap-video { height: var(--ap-video-h); aspect-ratio: 16 / 9; max-width: 100%; background: #000; position: relative; }
    .ap-video-vazio {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #94a3b8;
        font-size: 14px;
    }

    .ap-plyr,
    .ap-video :deep(.plyr),
    .ap-video :deep(.plyr__video-wrapper),
    .ap-video :deep(.plyr__video-embed) {
        width: 100% !important;
        height: 100% !important;
    }

    .ap-video :deep(.plyr__video-wrapper),
    .ap-video :deep(.plyr__video-embed) {
        padding: 0 !important;
        position: relative !important;
        background: #000;
    }

    .ap-video :deep(video),
    .ap-video :deep(iframe) {
        width: 100% !important;
        height: 100% !important;
        border: 0;
        display: block;
        background: #000;
        object-fit: contain;
    }

    .ap-video :deep(.plyr__video-embed iframe) {
        position: absolute !important;
        inset: 0;
    }

    .ap-barra-aula {
        position: sticky;
        top: 0;
        z-index: 2;
        background: var(--ap-superficie, #fff);
        border-bottom: 1px solid var(--ap-borda);
        padding: 0 32px;
    }

    .ap-barra-linha { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 20px 0 12px; }
    .ap-titulo-bloco { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .ap-secao-label { font-size: 12px; font-weight: 500; color: var(--ap-muted); }
    .ap-titulo-linha { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .ap-titulo { margin: 0; font-size: 24px; line-height: 1.3; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    .ap-selo {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        font-weight: 600;
        color: var(--ap-sucesso-texto);
        background: var(--ap-sucesso-suave);
        padding: 3px 10px;
        border-radius: 999px;
    }

    .ap-selo svg { width: 12px; height: 12px; stroke-width: 3; }
    .ap-acoes { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

    .ap-abas { display: flex; gap: 24px; }
    .ap-aba {
        min-height: 44px;
        padding: 0 2px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 0;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        background: none;
        color: var(--ap-muted);
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
    }

    .ap-aba:hover { color: var(--ap-texto); }
    .ap-aba.is-ativa { color: var(--ap-texto); border-bottom-color: var(--ap-primaria); }
    .ap-contador { font-size: 11px; padding: 1px 8px; border-radius: 999px; background: #f1f5f9; color: var(--ap-texto-2); }

    .ap-btn {
        min-height: 44px;
        padding: 0 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: 1px solid transparent;
        border-radius: 10px;
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .ap-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .ap-btn--sm { min-height: 40px; font-size: 13px; padding: 0 14px; }
    .ap-btn--bloco { width: 100%; }
    .ap-btn--primario { background: var(--ap-primaria); color: #fff; }
    .ap-btn--primario:hover:not(:disabled) { background: var(--ap-primaria-hover); }
    .ap-btn--contorno { background: var(--ap-superficie, #fff); border-color: var(--ap-borda); color: var(--ap-texto-2); }
    .ap-btn--contorno:hover:not(:disabled) { background: var(--ap-fundo); }
    .ap-btn--contorno.is-concluida { background: var(--ap-sucesso-suave); border-color: #a7f3d0; color: var(--ap-sucesso-texto); }

    .ap-btn-icone {
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 8px;
        background: none;
        color: var(--ap-muted);
        cursor: pointer;
    }

    .ap-btn-icone:hover { background: #fef2f2; color: var(--ap-perigo); }
    .ap-btn-icone svg { width: 16px; height: 16px; }
    .ap-link { border: 0; background: none; padding: 0; font: inherit; font-size: 12px; font-weight: 600; color: var(--ap-primaria-texto); cursor: pointer; }

    .ap-main button:focus-visible,
    .ap-main input:focus-visible {
        outline: 2px solid var(--ap-primaria);
        outline-offset: 2px;
    }

    .ap-conteudo { padding: 28px 32px 48px; flex-grow: 1; }
    .ap-card { background: var(--ap-superficie, #fff); border: 1px solid var(--ap-borda); border-radius: 14px; }
    button.ap-card { font: inherit; color: inherit; text-align: left; cursor: pointer; }
    .ap-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--ap-borda-suave); }
    .ap-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 20px;
        border-top: 1px solid var(--ap-borda-suave);
        background: var(--ap-fundo);
        border-radius: 0 0 14px 14px;
    }

    .ap-entre { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .ap-visao { display: flex; flex-direction: column; gap: 28px; }
    .ap-visao-topo { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: 24px; align-items: stretch; }

    .ap-trilha { padding: 28px; display: flex; flex-direction: column; gap: 16px; }
    .ap-trilha-cabecalho { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
    .ap-trilha-titulos { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
    .ap-trilha-titulo { margin: 0; font-size: 20px; line-height: 1.35; font-weight: 700; }
    .ap-trilha-descricao { margin: 0; max-width: 820px; font-size: 15px; line-height: 1.7; color: var(--ap-texto-3); white-space: pre-line; }
    .ap-trilha-dados {
        margin: 4px 0 0;
        padding-top: 20px;
        border-top: 1px solid var(--ap-borda-suave);
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px;
    }
    .ap-dado { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .ap-dado > div { display: flex; flex-direction: column; min-width: 0; }
    .ap-dado dt { font-size: 12px; color: var(--ap-muted); }
    .ap-dado dd { margin: 0; font-size: 14px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ap-dado-icone {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
        border-radius: 10px;
        background: #f1f5f9;
        color: var(--ap-texto-3);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .ap-progresso-card { padding: 28px; display: flex; flex-direction: column; gap: 18px; }
    .ap-progresso-numero { display: flex; align-items: baseline; gap: 10px; }
    .ap-progresso-numero strong { font-size: 40px; line-height: 1; font-weight: 700; }
    .ap-progresso-numero span { font-size: 14px; color: var(--ap-texto-3); }
    .ap-progresso { height: 6px; background: var(--ap-borda); border-radius: 999px; overflow: hidden; }
    .ap-progresso--lg { height: 8px; }
    .ap-progresso-fill { height: 100%; background: var(--ap-sucesso); border-radius: 999px; transition: width 0.3s ease; }
    .ap-progresso-dados { margin: 0; display: flex; flex-direction: column; gap: 10px; font-size: 13px; }
    .ap-progresso-dados > div { display: flex; justify-content: space-between; gap: 12px; }
    .ap-progresso-dados dt { color: var(--ap-muted); }
    .ap-progresso-dados dd { margin: 0; font-weight: 600; text-align: right; }

    .ap-proximas { display: flex; flex-direction: column; gap: 14px; }
    .ap-proximas-lista { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }

    .ap-vazio-texto { display: flex; flex-direction: column; gap: 2px; font-size: 13px; color: var(--ap-muted); }
    .ap-vazio-texto strong { font-size: 14px; color: var(--ap-texto-2); }
    .ap-vazio-icone {
        width: 44px;
        height: 44px;
        flex-shrink: 0;
        border-radius: 12px;
        background: #f1f5f9;
        color: var(--ap-muted);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .ap-vazio-icone svg { width: 22px; height: 22px; stroke-width: 1.8; }
    .ap-vazio-icone--lg { width: 52px; height: 52px; border-radius: 14px; }
    .ap-vazio-icone--lg svg { width: 24px; height: 24px; }
    .ap-vazio {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 56px 24px;
        text-align: center;
        font-size: 13px;
        line-height: 1.55;
        color: var(--ap-muted);
    }
    .ap-vazio strong { font-size: 15px; color: var(--ap-texto-2); }
    .ap-vazio > span:last-child { max-width: 360px; }

    .ap-proxima {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px;
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    .ap-proxima.is-seguinte { border-color: #bfd0ff; }
    .ap-proxima:hover { border-color: #bfd0ff; box-shadow: 0 4px 14px rgba(20, 99, 255, 0.08); }
    .ap-proxima-thumb {
        position: relative;
        width: 128px;
        aspect-ratio: 16 / 9;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 10px;
        background: #1e293b;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .ap-proxima-thumb svg { width: 20px; height: 20px; }
    .ap-proxima-duracao {
        position: absolute;
        right: 6px;
        bottom: 6px;
        padding: 1px 6px;
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.72);
        color: #fff;
        font-size: 11px;
        font-weight: 600;
    }
    .ap-proxima-texto { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .ap-proxima-tag { font-size: 12px; font-weight: 600; color: var(--ap-muted); }
    .ap-proxima.is-seguinte .ap-proxima-tag { color: var(--ap-primaria-texto); }
    .ap-proxima-titulo {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
        color: var(--ap-texto);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .ap-fim { display: flex; align-items: center; gap: 16px; padding: 20px 24px; background: var(--ap-sucesso-suave); border-color: #a7f3d0; }
    .ap-fim .ap-vazio-icone { background: var(--ap-superficie, #fff); color: var(--ap-sucesso-texto); }

    .ap-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; align-items: start; }

    .ap-editor :deep(.ql-toolbar) {
        border: 0;
        border-bottom: 1px solid var(--ap-borda-suave);
        background: transparent;
    }
    .ap-editor :deep(.ql-container) {
        border: 0;
        min-height: 160px;
        font-family: inherit;
        font-size: 14px;
    }
    .ap-editor :deep(.ql-editor) { min-height: 160px; padding: 16px 20px; }
    .ap-editor :deep(.ql-editor.ql-blank::before) { font-style: normal; color: var(--ap-muted); }

    .ap-tempo-campo { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 13px; color: var(--ap-texto-3); }
    .ap-tempo-campo svg { width: 16px; height: 16px; }
    .ap-tempo-campo input {
        width: 80px;
        height: 32px;
        box-sizing: border-box;
        padding: 0 8px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: var(--ap-superficie, #fff);
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        color: var(--ap-primaria-texto);
        text-align: center;
    }
    .ap-tempo-campo input[aria-invalid='true'] { border-color: var(--ap-perigo); }
    .ap-erro { font-size: 12px; color: var(--ap-perigo); }

    .ap-notas { list-style: none; margin: 0; padding: 0; max-height: 520px; overflow-y: auto; }
    .ap-nota { display: flex; align-items: flex-start; gap: 14px; padding: 16px 20px; }
    .ap-nota + .ap-nota { border-top: 1px solid var(--ap-borda-suave); }
    .ap-chip-tempo {
        flex-shrink: 0;
        height: 28px;
        padding: 0 10px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        border: 0;
        border-radius: 8px;
        background: var(--ap-primaria-suave);
        color: var(--ap-primaria-texto);
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
    }
    .ap-chip-tempo svg { width: 10px; height: 10px; }
    .ap-chip-tempo:hover { filter: brightness(0.96); }
    .ap-nota-corpo { flex-grow: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
    .ap-nota-texto { margin: 0; font-size: 14px; line-height: 1.55; color: var(--ap-texto-2); overflow-wrap: anywhere; }
    .ap-nota-texto :deep(p) { margin: 0 0 6px; }
    .ap-nota-texto :deep(p:last-child) { margin-bottom: 0; }

    .ap-materiais-topo { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
    .ap-busca { position: relative; display: flex; align-items: center; }
    .ap-busca svg { position: absolute; left: 12px; width: 16px; height: 16px; color: var(--ap-muted); pointer-events: none; }
    .ap-busca input {
        width: 260px;
        height: 40px;
        box-sizing: border-box;
        padding: 0 12px 0 36px;
        border: 1px solid #cbd5e1;
        border-radius: 10px;
        background: var(--ap-superficie, #fff);
        font: inherit;
        font-size: 13px;
        color: var(--ap-texto);
    }

    .ap-materiais { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
    .ap-material {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    .ap-material:hover { border-color: #bfd0ff; box-shadow: 0 4px 14px rgba(20, 99, 255, 0.08); }
    .ap-material:hover .ap-material-baixar { background: var(--ap-primaria-suave); }
    .ap-tipo {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1px;
        background: #f1f5f9;
        color: var(--ap-texto-3);
    }
    .ap-tipo svg { width: 16px; height: 16px; }
    .ap-tipo span { font-size: 9px; font-weight: 700; line-height: 1; }
    .ap-tipo--pdf { background: #fef2f2; color: #b91c1c; }
    .ap-tipo--doc { background: var(--ap-primaria-suave); color: var(--ap-primaria-texto); }
    .ap-tipo--xls { background: var(--ap-sucesso-suave); color: var(--ap-sucesso-texto); }
    .ap-tipo--ppt { background: #fff7ed; color: #c2410c; }
    .ap-tipo--img { background: #f5f3ff; color: #6d28d9; }
    .ap-material-texto { flex-grow: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .ap-material-nome { font-size: 14px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .ap-material-baixar {
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--ap-primaria-texto);
    }

    @media (max-width: 1400px) {
        .ap-visao-topo { grid-template-columns: minmax(0, 1fr) 320px; }
        .ap-trilha-dados { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .ap-proximas-lista { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .ap-proximas-lista li:nth-child(3) { display: none; }
    }

    @media (max-width: 1200px) {
        .ap-barra-linha { flex-direction: column; align-items: stretch; }
        .ap-acoes { justify-content: flex-end; }
        .ap-grid-2,
        .ap-visao-topo,
        .ap-proximas-lista { grid-template-columns: minmax(0, 1fr); }
        .ap-proximas-lista li:nth-child(3) { display: list-item; }
    }

    @media (max-width: 1024px) {
        .ap-main { height: auto; overflow: visible; }
        .ap-barra-aula { top: var(--appbar-height, 65px); }
        .ap-video { height: auto; width: 100%; aspect-ratio: 16 / 9; }
    }

    @media (max-width: 640px) {
        .ap-barra-aula,
        .ap-conteudo { padding-left: 16px; padding-right: 16px; }
        .ap-titulo { font-size: 20px; white-space: normal; }
        .ap-acoes .ap-btn { flex: 1; padding: 0 10px; }
        .ap-ocultar-sm { display: none; }
        .ap-abas { gap: 16px; overflow-x: auto; scrollbar-width: none; }
        .ap-abas::-webkit-scrollbar { display: none; }
        .ap-busca,
        .ap-busca input { width: 100%; }
        .ap-materiais { grid-template-columns: minmax(0, 1fr); }
        .ap-proxima-thumb { width: 96px; }
        .ap-trilha-cabecalho { flex-direction: column; }
        .ap-trilha-dados { grid-template-columns: minmax(0, 1fr); }
        .ap-trilha,
        .ap-progresso-card { padding: 20px; }
        .ap-card-footer { flex-direction: column; align-items: stretch; }
    }

    [data-bs-theme="dark"] .ap-contador { background: rgba(148, 163, 184, 0.16); }
    [data-bs-theme="dark"] .ap-vazio-icone,
    [data-bs-theme="dark"] .ap-dado-icone { background: rgba(148, 163, 184, 0.12); }
    [data-bs-theme="dark"] .ap-btn-icone:hover { background: rgba(248, 113, 113, 0.12); }
    [data-bs-theme="dark"] .ap-btn--contorno.is-concluida { border-color: rgba(52, 211, 153, 0.4); }
    [data-bs-theme="dark"] .ap-fim { border-color: rgba(52, 211, 153, 0.4); }
    [data-bs-theme="dark"] .ap-tempo-campo input,
    [data-bs-theme="dark"] .ap-busca input { border-color: #475569; }
    [data-bs-theme="dark"] .quill-wrapper :deep(.ql-stroke) { stroke: #cbd5e1; }
    [data-bs-theme="dark"] .quill-wrapper :deep(.ql-fill) { fill: #cbd5e1; }
    [data-bs-theme="dark"] .quill-wrapper :deep(.ql-picker) { color: #cbd5e1; }
    [data-bs-theme="dark"] .quill-wrapper :deep(.ql-editor) { color: #e2e8f0; }
</style>
